import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from './LoadingSpinner';
import StoryCard from './StoryCard';
import StoryReels from './StoryReels';
import ReelsCarousel from './ReelsCarousel';

const PAGE_SIZE = 20;

const CommunityStories = ({ organizationId, viewMode = 'stories' }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);

  const fetchStoriesForOrg = async (reset = false) => {
    if (!organizationId) {
      setStories([]);
      setLoading(false);
      return;
    }

    if (reset) {
      setLoading(true);
      setStories([]);
      setLastVisible(null);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      console.log('[CommunityStories] Fetching stories for org:', organizationId, reset ? '(reset)' : '(load more)');
      
      // Query the 'stories' subcollection within the organization document
      // Stories now have FULL data in organization collections (not just metadata)
      const storiesRef = collection(db, 'organizations', organizationId, 'stories');
      
      // Try to query with status filter, but if it fails (no index), fetch all and filter client-side
      let storiesSnapshot;
      try {
        let storiesQuery = query(
          storiesRef,
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
        
        // Add pagination cursor if loading more
        if (!reset && lastVisible) {
          storiesQuery = query(
            storiesRef,
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(PAGE_SIZE)
          );
        }
        
        storiesSnapshot = await getDocs(storiesQuery);
      } catch (queryError) {
        console.log('[CommunityStories] Query with filters failed, fetching all and filtering client-side:', queryError);
        // Fallback: fetch all stories and filter client-side
        let fallbackQuery = query(storiesRef, orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
        if (!reset && lastVisible) {
          fallbackQuery = query(storiesRef, orderBy('createdAt', 'desc'), startAfter(lastVisible), limit(PAGE_SIZE));
        }
        storiesSnapshot = await getDocs(fallbackQuery);
      }

        if (storiesSnapshot.empty) {
          console.log('[CommunityStories] No stories found in collection');
          setStories([]);
          setLoading(false);
          return;
        }

        console.log('[CommunityStories] Found', storiesSnapshot.docs.length, 'stories in collection');

        // Stories in organization collections now have FULL data
        // Validate that each story is actually shared with this organization
        // Use Promise.all since we need to fetch media from user collection for some stories
        const validStoriesPromises = storiesSnapshot.docs.map(async (storyDoc) => {
            const storyData = storyDoc.data();
            
            console.log('[CommunityStories] Checking story', storyDoc.id, {
              hasText: !!storyData.text,
              hasAuthorId: !!storyData.authorId,
              status: storyData.status,
              orgs: storyData.sharing?.organizations,
              hasSharing: !!storyData.sharing
            });
            
            // Validate the story is shared with this organization
            // Handle both array and object formats (Firestore sometimes deserializes arrays as objects)
            let orgs = storyData.sharing?.organizations;
            if (!orgs) {
              console.log('[CommunityStories] ❌ Story', storyDoc.id, 'no sharing.organizations field');
              return null;
            }
            
            // Convert object to array if needed (e.g., {0: 'org1', 1: 'org2'} -> ['org1', 'org2'])
            if (!Array.isArray(orgs)) {
              if (typeof orgs === 'object') {
                orgs = Object.values(orgs);
                console.log('[CommunityStories] Converted orgs object to array:', orgs);
              } else {
                console.log('[CommunityStories] ❌ Story', storyDoc.id, 'sharing.organizations is not array or object:', typeof orgs);
                return null;
              }
            }
            
            if (!orgs.includes(organizationId)) {
              console.log('[CommunityStories] ❌ Story', storyDoc.id, 'not shared with org', organizationId, '- orgs:', orgs);
              return null;
            }
            
            // Check status - be lenient: if status exists, it must be 'active', otherwise allow (old stories)
            if (storyData.status !== undefined && storyData.status !== 'active') {
              console.log('[CommunityStories] ❌ Story', storyDoc.id, 'status is', storyData.status, '(not active)');
              return null;
            }
            
            // Check required fields (matching mobile StoriesFeed validation)
            // text is required, authorId should exist but we can use authorId from the data
            if (!storyData.text || typeof storyData.text !== 'string' || storyData.text.trim() === '') {
              console.log('[CommunityStories] ❌ Story', storyDoc.id, 'missing or invalid text field');
              return null;
            }
            
            // authorId should exist, but if it doesn't, try to get it from author field or skip
            const authorId = storyData.authorId || storyData.author?.id;
            if (!authorId) {
              console.log('[CommunityStories] ❌ Story', storyDoc.id, 'missing authorId');
              return null;
            }

            // Extract metrics from nested object and map to StoryCard's expected format
            const metrics = storyData.metrics || {};
            
            // Get all media-related fields (try multiple possible field names)
            let photoURL = storyData.photoURL || storyData.photo || storyData.imageURL || storyData.image || null;
            let playableURL = storyData.playableURL || storyData.videoURL || storyData.video || storyData.videoUrl || null;
            let posterURL = storyData.posterURL || storyData.poster || storyData.thumbnailURL || null;
            let storagePath = storyData.storagePath || storyData.videoStoragePath || null;
            
            // If media URLs are missing, try fetching from user collection as fallback
            if ((!photoURL && !playableURL) && authorId) {
              try {
                const userStoryDoc = await getDoc(doc(db, 'users', authorId, 'stories', storyDoc.id));
                if (userStoryDoc.exists) {
                  const userStoryData = userStoryDoc.data();
                  console.log('[CommunityStories] Fetched media from user collection for story', storyDoc.id);
                  photoURL = photoURL || userStoryData.photoURL || userStoryData.photo || null;
                  playableURL = playableURL || userStoryData.playableURL || userStoryData.videoURL || userStoryData.video || null;
                  posterURL = posterURL || userStoryData.posterURL || userStoryData.poster || null;
                  storagePath = storagePath || userStoryData.storagePath || userStoryData.videoStoragePath || null;
                }
              } catch (fetchError) {
                console.log('[CommunityStories] Could not fetch from user collection (non-fatal):', fetchError);
              }
            }
            
            const fullStoryData = {
              ...storyData, // Spread all original fields first
              id: storyDoc.id,
              authorId: authorId, // Ensure authorId is set
              // author info is already in the story data (authorName, authorEmail, authorPhotoURL)
              author: storyData.authorId ? {
                id: storyData.authorId,
                name: storyData.authorName || storyData.author?.name || 'Anonymous',
                email: storyData.authorEmail || storyData.author?.email,
                photoURL: storyData.authorPhotoURL || storyData.author?.photoURL
              } : (storyData.author || {
                id: authorId,
                name: 'Anonymous'
              }),
              // Map metrics to StoryCard's expected format (both nested and top-level)
              metrics: metrics, // Keep nested format
              believersEncouraged: metrics.believersEncouraged || 0,
              newFollowers: metrics.newFollowers || 0,
              spiritualDiscussions: metrics.spiritualDiscussions || 0,
              heardGospel: metrics.heardGospel || 0,
              experiencedLove: metrics.experiencedLove || 0,
              // Legacy metric names (if needed)
              livesChanged: metrics.livesChanged || 0,
              soulsSaved: metrics.soulsSaved || 0,
              disciplesMade: metrics.disciplesMade || 0,
              churchesPlanted: metrics.churchesPlanted || 0,
              // Ensure photo and video URLs are available (with all possible field names)
              photoURL: photoURL,
              playableURL: playableURL,
              videoURL: playableURL, // Also set videoURL for compatibility
              posterURL: posterURL,
              storagePath: storagePath,
              // Use createdAt for timestamp (StoryCard expects 'timestamp')
              timestamp: storyData.createdAt || storyData.timestamp || null
            };

            console.log('[CommunityStories] ✅ Valid story:', storyDoc.id, {
              text: fullStoryData.text?.substring(0, 50),
              hasPhoto: !!fullStoryData.photoURL,
              hasVideo: !!fullStoryData.playableURL,
              hasPoster: !!fullStoryData.posterURL,
              photoURL: fullStoryData.photoURL,
              playableURL: fullStoryData.playableURL,
              videoURL: fullStoryData.videoURL,
              storagePath: fullStoryData.storagePath,
              allKeys: Object.keys(fullStoryData).filter(k => k.includes('photo') || k.includes('video') || k.includes('URL') || k.includes('storage'))
            });
            return fullStoryData;
          });
        
        const validStories = (await Promise.all(validStoriesPromises)).filter(Boolean);
        
        // Sort by createdAt descending (if not already sorted)
        validStories.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
          return bTime - aTime;
        });
        
        console.log('[CommunityStories] Valid stories after filtering:', validStories.length);
        
        // Update pagination state
        if (storiesSnapshot.docs.length > 0) {
          const lastDoc = storiesSnapshot.docs[storiesSnapshot.docs.length - 1];
          setLastVisible(lastDoc);
          setHasMore(storiesSnapshot.docs.length === PAGE_SIZE);
        } else {
          setHasMore(false);
        }
        
        // Update stories
        if (reset) {
          setStories(validStories);
        } else {
          setStories(prev => [...prev, ...validStories]);
        }

      } catch (error) {
        console.error("Error fetching community stories:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

  useEffect(() => {
    fetchStoriesForOrg(true);
  }, [organizationId]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchStoriesForOrg(false);
    }
  };

  if (loading) {
    return <div className="py-8"><LoadingSpinner /></div>;
  }

  // Filter video stories for reels
  const videoStories = stories.filter(story => story.playableURL);

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {viewMode === 'reels' ? (
        // Reels View - TikTok-style carousel
        <div className="flex justify-center">
          {videoStories.length > 0 ? (
            <ReelsCarousel stories={videoStories} />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No video stories have been shared in this community yet.</p>
              <p className="text-sm mt-2">Share a video story to see it here!</p>
            </div>
          )}
        </div>
      ) : (
        // Stories View - Traditional layout
        <div className="space-y-6">
          {stories.length > 0 ? (
            <>
              {stories.map(story => (
                <StoryCard key={story.id} story={story} />
              ))}
              {hasMore && (
                <div className="flex justify-center pt-4 pb-2">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium text-sm sm:text-base"
                  >
                    {loadingMore ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </span>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-8">No stories have been shared in this community yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunityStories; 